<template>
  <component is="style" type="text/css">
    {{ state.customFontStyling ? state.customFontStyling : '' }}
    {{ state.globalStyling ? state.globalStyling : '' }}
    {{ state.campaignStyling ? state.campaignStyling : '' }}
    {{ onlyDevMode ? state.config?.resources.css : '' }}
  </component>

  <teleport to="body">
    <transition name="long-fade" mode="out-in" appear>
      <Preloader v-if="!contentRendered && !isPopup && !hidePreloader" :data="preloaderStyles" />
    </transition>
  </teleport>

  <div
    class="campaign"
    :style="campaignStyle"
    :class="{
      'campaign--enter': enterAnimation,
      'campaign--enter-instant': hidePreloader
    }"
    @click="onCampaignClick"
  >
    <!-- Transition can be found in animations.scss -->
    <div v-if="ready" class="campaign__pages">
      <div class="campaign__page container-fluid">
        <div v-if="isPopup" class="positioner">
          <!--LFSection is a global component, so we dont need to import it -->
          <LFSection v-for="section in sections" :key="section.id" :model="section" />
        </div>
        <LFSection v-for="section in sections" v-else :key="section.id" :model="section" />
        <AddSectionGridContainer v-if="state.isEditModeActive && state.edit?.enabled && !isPopup" />
      </div>
    </div>
  </div>

  <!--
    These are useHead() component to communicate
    with head and body tags, as you directly in Vue, doesnt have access to it
    So this acts as a proxy of some sort.
  -->
  <Head>
    <title>{{ campaignTitle }}</title>
    <link v-if="campaignState.config?.favicon" rel="icon" type="image/png" :href="campaignState.config?.favicon" />
    <body :class="utilityStoreState.bodyClassList"></body>
  </Head>

  <component :is="gameInitializerComp" v-if="hasGameInitializer" />

  <ClientOnly>
    <!-- Toolbar -->
    <teleport to="body">
      <component :is="LFToolbar" v-if="showToolbar" @closeToolbar="onCloseToolbar" @openToolbar="onOpenToolbar" />
    </teleport>

    <!-- Only show Navigators if we are on route with params /edit/ -->
    <teleport to="body">
      <transition name="slide-fade" mode="out-in">
        <component
          :is="LFNavigator"
          v-if="
            $route.query.edit === '1' &&
            state.isEditModeActive &&
            globalState.editing?.isNavigatorActive &&
            state.edit?.enabled &&
            !isTemplatePreview
          "
          :is-docked="isDocked"
          @requestDocking="onRequestNavigatorDock"
          @setDocking="onSetDocking"
        />
      </transition>
    </teleport>

    <teleport to="body">
      <transition name="slide-fade" mode="out-in">
        <component
          :is="LFNavigator"
          v-if="state.isEditModeActive && state.edit?.enabled && isNavigatorDockRequest"
          :is-dock-request="isNavigatorDockRequest"
        />
      </transition>
    </teleport>

    <teleport :to="isDemo && state.isEditModeActive ? '.site-editor__viewer' : 'body'">
      <ScrollIndicator v-if="contentRendered && isScrollIndicatorEnabled" :color="colorScrollIndicator" />
    </teleport>

    <teleport :to="isDemo ? '.site-editor__viewer' : 'body'">
      <Popover
        v-if="ready"
        :key="`popover`"
        :page-id="Number($route.query.pageId)"
        :background-color="state?.layout?.backgroundColor"
        :text-color="state?.layout?.textColor"
        :pages="state?.popovers"
      />
    </teleport>

    <teleport to="#app">
      <Menu />
    </teleport>
  </ClientOnly>

  <Integration v-for="integration in integrations" :key="integration.state.id" :model="integration" />

  <teleport to="body">
    <transition name="modal" :appear="true">
      <Prompt
        v-if="showPrompt"
        :heading="promptHeading"
        :description="promptDescription"
        :buttons="promptButtons"
        @buttonClicked="handlePromptButtonClicked"
        @hidePrompt="showPrompt = false"
      />
    </transition>
  </teleport>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  computed,
  defineAsyncComponent,
  watch,
  onBeforeMount,
  Component,
  nextTick,
  getCurrentInstance,
  onMounted
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { CampaignData } from '@/typings/interfaces/data/campaign';
import { CampaignModel } from '@/models/CampaignModel';
import ScrollIndicator from '@/components/ui/ScrollIndicator.vue';
import { ActiveElementTypes, AlignContentType, SectionType } from '@/typings/enums/enums';
import { getCookie, setCookie } from '@/utilities/CookieHelpers';
import { useStore } from '@/store';
import { HeadAttrs, HeadObject, useHead } from '@vueuse/head';
import { getDocumentHeight, inIframe, isSsr } from '@/utilities/Utilities';
import { getQueryParams, getSpecificQueryParam, parseQueryString } from '@/utilities/Url';
import Menu from '@/components/navigation/Menu.vue';
import { events } from '@/services/events';
import { recursivelyWaitForPromises } from '@/utilities/virtualDom';
import Popover from '@/components/components/popover/Popover.vue';
import { onResize } from '@/services/iframe-embed';
import Integration from './Integration.vue';
import { warmAudio } from '@/utilities/AudioPlayer';
import { IframeEventData, onEvent, sendEvent } from '@/utilities/iFrameUtils';
import ApplyReplacementTags from '@/components/ApplyReplacementTags.vue';
import DeviceRotater from '@/components/ui/DeviceRotater.vue';
import useEditOptions from '@/hooks/useEditOptions';
import Prompt from '@/components/components/prompt/Prompt.vue';
import { isBefore } from 'date-fns';
import { useToast, POSITION } from 'vue-toastification';
import ToastMessage from './ui/ToastMessage.vue';
import useFlow from '@/hooks/useFlow';
import useAnalytics from '@/hooks/useAnalytics';
import initializeSDK from '@/sdk';
import { appendScriptToBody, appendScriptToHead } from '@/utilities/Scripts';
import Preloader from '@/components/ui/Preloader.vue';
import { goToFictiveFlowPage } from '@/utilities/Flow';
import { ContentType } from '@/components/layout/section/SectionBaseModel';
import { getWithTimezone } from '@/utilities/DateHelpers';
import useDate from '@/hooks/useDate';

const AddSectionGridContainer = defineAsyncComponent({
  loader: () => import('@/components/components/editing/AddSectionGridContainer.vue')
});

/**
 * Match key with component - We cant just do one single import
 */
const gameInitializers: { [key: string]: () => Promise<Component> } = {
  popup: () => import('@/components/games/popup/View.vue'),
  popupv2: () => import('@/components/games/popup/View.vue'),
  campaign_link: () => import('@/components/games/campaign-link/GameInitializer.vue'),
  poll: () => import('@/components/games/poll/PollGameInitializer.vue'),
  dropgame: () => import('@/components/games/dropgame/DropgameGameInitializer.vue'),
  personality_test: () => import('@/components/games/personality-test/GameInitializer.vue'),
  calendar: () => import('@/components/games/calendar/GameInitializer.vue')
};

export default defineComponent({
  name: 'Campaign',
  components: {
    Preloader,
    Prompt,
    DeviceRotater,
    ApplyReplacementTags,
    Menu,
    Popover,
    ScrollIndicator,
    AddSectionGridContainer,
    Integration,
    ToastMessage
  },
  props: {
    data: {
      type: Object as PropType<CampaignData>,
      required: true
    },
    enterAnimation: {
      type: Boolean,
      default: true
    }
  },
  setup: (props, context) => {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const {
      initHotKeys,
      unbindHotkeys,
      showPrompt,
      promptHeading,
      promptDescription,
      promptButtons,
      handlePromptButtonClicked
    } = useEditOptions(context);
    const model = new CampaignModel(props.data);
    const state = model.state;
    const campaignState = store.getters['campaign/state'];
    const isNavigatorDockRequest = ref(false);
    const isDocked = ref(getCookie('lf-campaign-navigator-is-docked') === 'true' || false);
    const currentPopover = ref<number | undefined | null>(null);
    const hasOpenedCacheInfoInSession = ref(false);
    const hasSeenCacheInfo = ref(getCookie('cache-tooltip') === '1');
    const hidePreloader = ref(false);
    const ready = ref(false);
    const contentRendered = ref(false);
    const isPopup = model.state.isPopup;
    const queryParams = getQueryParams();
    const isTemplatePreview = queryParams['template'] === 'overlay';

    const isDemo = computed(() => {
      if (isSsr()) return false;
      return (store.getters['utility/path'] ?? window.location.href).includes('/campaign/view/demo');
    });

    const isSimulating = computed(() => {
      return store.getters['global/currentDevice'];
    });

    // @ts-ignore // We need to find a way to type this
    const onlyDevMode = (import.meta as unknown).env.MODE === 'development';

    if (state.isEditModeActive) {
      store.commit('editing/setNavigatorActiveState', true);
    }

    store.subscribe(() => {
      currentPopover.value = store.getters['campaign/popoverId'];
    });
    const params = getQueryParams();

    /**
     * We could have put this in CampaignModel yes. But the issue is that there might
     * be custom css or other places that could change the background.
     * So to ensure that it's always getting removed i decided to just do exactly
     * what the old one did and just append it ot the body style.
     *
     * Otherwise I'm open for suggestions (Dannie).
     */
    if (params['in-campaign-link'] && !isSsr()) {
      // Embedded campaigns in campaign link should not show a background
      document.body.style.cssText += 'background: transparent !important;';
    }

    if (params['hide-loader'] || params['in-campaign-link'] || params['template'] === 'overlay') {
      hidePreloader.value = true;
    }

    if (isDocked.value && campaignState.isEditModeActive) {
      store.dispatch('utility/addBodyClass', 'site--navigator-docked');
    }

    // Add a class for when we're in iframe so that we can style it accordingly
    if (inIframe()) {
      store.dispatch('utility/addBodyClass', 'site--iframe');
    }

    const dismissCacheTooltip = () => {
      if (state.config?.activeTo) {
        hasSeenCacheInfo.value = true;
      }
    };

    const showToolbar = computed(() => {
      return state.edit?.enabled && getSpecificQueryParam('in-campaign-link') !== '1';
    });

    const showCacheToolTip = computed(() => {
      if (hasSeenCacheInfo.value) return false;

      const today = useDate().getDate();

      if (state.config?.activeFrom) {
        const activeFromDate = getWithTimezone(state.config.activeFrom);

        if (state.config?.activeFrom && isBefore(today, activeFromDate)) {
          return false;
        }
      }

      if (state.config?.activeTo) {
        const activeToDate = getWithTimezone(state.config.activeTo);
        setCookie('cache-tooltip', '1', { expire: activeToDate });
        return isBefore(today, activeToDate);
      }

      return false;
    });

    const fonts = computed<{ link: HeadAttrs[] }>(() => {
      const customLinks = (store.getters['utility/fonts'] as string[]).filter((font) => {
        return campaignState.config?.customFonts.find((custom) => {
          return custom.fontFamily === font && custom.css !== '';
        });
      });

      const links = (store.getters['utility/fonts'] as string[]).filter((font) => {
        return campaignState.config?.customFonts.every((custom) => {
          return custom.fontFamily !== font;
        });
      });

      links.push(...customLinks);

      return {
        link: links.map((font) => {
          return {
            type: 'text/css',
            rel: 'stylesheet',
            href:
              'https://fonts.googleapis.com/css?family=' +
              encodeURIComponent(font) +
              ':200,200i,300,300i,400,400i,500,500i,600,700,700i,800,800i,900,900i&display=swap',
            key: font
          };
        })
      };
    });

    const onCampaignClick = () => {
      warmAudio();
      if (state.isPreviewModeActive && state.isEditModeActive) {
        state.isPreviewModeActive = false;
      }
    };

    const onOpenToolbar = () => {
      state.isEditModeActive = true;
    };

    const onCloseToolbar = () => {
      state.isEditModeActive = false;
      store.dispatch('editing/setNavigatorActiveState', true);
    };

    const sections = computed(() => {
      return state.sections.filter((section) => {
        const visibilityConditions = section.state.config.settings.state.advanced?.visibilityCondition;

        if (section.state.config.hidden) {
          return false;
        }

        /**
         * The following code is related to the iframe-games addon which can have a parameter of onlyflow=1
         */
        if (
          typeof params['onlyflow'] !== 'undefined' &&
          params['onlyflow'] &&
          section.getAddons('gameflow').length !== 1
        ) {
          return false;
        }

        return visibilityConditions ? visibilityConditions.check() : true;
      });
    });

    const isScrollIndicatorEnabled = computed(() => {
      const scrollIndicatorData = state.layout?.scrollIndicator;
      return scrollIndicatorData?.enable;
    });

    const colorScrollIndicator = computed(() => {
      const scrollIndicatorData = state.layout?.scrollIndicator;
      const defaultColor = '#000';
      return scrollIndicatorData?.color || defaultColor;
    });

    const campaignStyle = computed(() => {
      const campaignGrid = state.layout?.grid;
      const campaignPadding = store.state.utility.campaignPadding;
      if (campaignGrid) {
        const campaignAlignment = campaignGrid?.align;
        let margin: string | undefined;

        if (campaignAlignment === AlignContentType.CENTER) {
          margin = '0 auto';
        }
        if (campaignAlignment === AlignContentType.RIGHT) {
          margin = '0 0 0 auto';
        }

        return {
          ...(margin && { margin: margin }),
          paddingTop: `${campaignPadding}px`,
          ...(campaignAlignment && {})
        };
      }
      return {};
    });

    const onRequestNavigatorDock = (requestValue: boolean) => {
      isNavigatorDockRequest.value = requestValue;
    };

    const onSetDocking = (dockedState: boolean) => {
      // No longer a request
      isNavigatorDockRequest.value = false;
      // Set the docked state
      isDocked.value = dockedState;
    };

    const initializeDevCustomScripts = () => {
      if (campaignState.config?.resources.head) {
        appendScriptToHead(campaignState.config?.resources.head);
      }
      if (campaignState.config?.resources.before_body_start) {
        appendScriptToBody(campaignState.config?.resources.before_body_start);
      }
      if (campaignState.config?.resources.before_body_close) {
        appendScriptToBody(campaignState.config?.resources.before_body_close);
      }
    };

    onMounted(async () => {
      if (campaignState.isEditModeActive) {
        initHotKeys();
      }

      if (onlyDevMode) {
        initializeDevCustomScripts();
        initializeSDK();
      }
    });

    store.subscribe(({ type }) => {
      if (type === 'editing/setNavigatorTabCategory') {
        const activeTabCategory = store.getters['editing/navigatorTabCategory'];

        if (activeTabCategory === SectionType.SECTION) {
          store.dispatch('utility/addBodyClass', 'site--navigator-category-sections');
          store.dispatch('utility/removeBodyClass', 'site--navigator-category-flowpages');
          store.dispatch('utility/removeBodyClass', 'site--navigator-category-popovers');
        }

        if (activeTabCategory === SectionType.FLOWPAGE) {
          store.dispatch('utility/addBodyClass', 'site--navigator-category-flowpages');
          store.dispatch('utility/removeBodyClass', 'site--navigator-category-sections');
          store.dispatch('utility/removeBodyClass', 'site--navigator-category-popovers');
        }

        if (activeTabCategory === SectionType.POPOVER) {
          store.dispatch('utility/removeBodyClass', 'site--navigator-category-sections');
          store.dispatch('utility/removeBodyClass', 'site--navigator-category-flowpages');
          store.dispatch('utility/addBodyClass', 'site--navigator-category-popovers');
        }
      } else if (type === 'editing/setNavigatorActiveState') {
        if (store.getters['editing/isNavigatorActive']) {
          store.dispatch('utility/removeBodyClass', 'site--navigator-disabled');
        } else {
          store.dispatch('utility/addBodyClass', 'site--navigator-disabled');
          store.dispatch('utility/removeBodyClass', 'site--navigator-docked');
        }
      }
    });

    const initClearCacheInfo = () => {
      hasOpenedCacheInfoInSession.value = true;
      const message =
        'This campaign is live. You can continue to edit it, <strong>but you will need to <span class="highlight">clear cache</span> to see changes right away on the Live URL.</strong>';
      toast.info(
        { component: ToastMessage, props: { tip: true, message: message } },
        {
          position: POSITION.BOTTOM_LEFT,
          onClose: () => {
            dismissCacheTooltip();
          },
          timeout: 10000
        }
      );
    };

    watch(
      () => campaignState.isEditModeActive,
      () => {
        if (campaignState.isEditModeActive) {
          initHotKeys();
          if (showCacheToolTip.value && isDemo.value && !hasOpenedCacheInfoInSession.value) {
            initClearCacheInfo();
          }
        }
        if (!campaignState.isEditModeActive) {
          unbindHotkeys();
        }
      }
    );

    watch(
      () => props.data,
      () => {
        model.setData(props.data);
      }
    );

    watch(
      () => currentPopover.value,
      () => {
        /**
         * If there is no popover open, remove the parameter pageId from URL, else add it to the url
         */
        if (!currentPopover.value) {
          const query = { ...route.query };
          delete query.pageId;
          router.push({ query });
        } else {
          router.push({
            query: {
              ...router.currentRoute.value.query,
              pageId: currentPopover.value
            }
          });
        }
      }
    );

    /**
     * Handle situations where we call go-to-next-flowpage, but there is no more.
     */
    watch(
      () => store.getters['campaign/noMoreFlowPages'],
      () => {
        const noMoreFlowPages = store.getters['campaign/noMoreFlowPages'];
        if (!isSsr() && !params['in-campaign-link'] && campaignState.config?.gameAlias !== 'landingpage') {
          let documentHeight = getDocumentHeight();
          if (noMoreFlowPages) {
            goToFictiveFlowPage('No more flowpages', {
              type: ContentType.TEXT,
              content: `<div class="row" style="height: ${documentHeight}px">
                          <h2></h2>
                          <div class="message-body"></div>
                        </div>`
            });
          }
        }
      }
    );

    watch(
      () => route.query.pageId,
      () => {
        if (route.query.pageId && !campaignState.isEditModeActive) {
          events.emit('popoverShow', Number(route.query.pageId));
        } else {
          if (!campaignState.isEditModeActive) {
            events.emit('popoverHide');
          }
        }
      },
      {
        immediate: false
      }
    );

    useHead(fonts);

    if (typeof window !== 'undefined') {
      useHead({
        htmlAttrs: {
          class: 'ontouchstart' in window ? 'touch' : 'no-touch'
        }
      });
    }

    onBeforeMount(() => {
      if (route.params.edit) {
        state.isEditModeActive = true;
      }
    });

    const hasGameInitializer =
      model.state.config?.gameAlias && typeof gameInitializers[model.state.config?.gameAlias] !== 'undefined';

    let gameInitReadyPromiseResolve: (() => void) | undefined;

    const gameInitReadyPromise = new Promise<void>((resolve) => {
      gameInitReadyPromiseResolve = resolve;

      if (!hasGameInitializer) {
        gameInitReadyPromiseResolve();
      }
    });

    /**
     * You can read more about the game initializer component in the README.md under the popup section.
     */
    const gameInitializerComp = defineAsyncComponent({
      loader: async () => {
        if (model.state.config?.gameAlias && typeof gameInitializers[model.state.config?.gameAlias] !== 'undefined') {
          const component = await gameInitializers[model.state.config?.gameAlias]();

          await nextTick(() => {
            if (gameInitReadyPromiseResolve) {
              gameInitReadyPromiseResolve();
            }
          });

          return component;
        }

        throw new Error('Unrecognized game initializer component');
      }
    });

    let readyPromiseResolve: (() => void) | undefined;
    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    const currentInstance = getCurrentInstance();

    if (state.isEditModeActive && route.query.pageId) {
      const hidePopover = () => {
        store.commit('campaign/setPopoverId', null);
        events.emit('popoverHide');
      };
      hidePopover();
    }

    if (!isSsr()) {
      (async () => {
        // If game contains game initializer components we need to wait for it to load before we continue loading the
        // campaign.
        await gameInitReadyPromise;

        // For some reason 3 ticks is needed before our game initializer component is fully added.
        // Don't expect me to understand...
        await nextTick();
        await nextTick();
        await nextTick();

        // Ensures that game initializer & integrations can stall rendering of the page.
        // For integrations this will wait for the component to load & register what it needs to register.
        if (currentInstance?.vnode) {
          // Wait for onBeforeEnter upwards to 6 times. This allows async components to load other async components.
          await recursivelyWaitForPromises(currentInstance.vnode, 'onBeforeEnter', 6);
        }

        // Get initial flowpage. This is done here to prevent visibility condition issues
        // if integrations extend the visibility condition service.
        if (!store.getters['campaign/flowId']) {
          store.commit('campaign/setCurrentFlowId', useFlow().getNextFlowPage());
        }

        await nextTick();
        ready.value = true;

        if (readyPromiseResolve) {
          readyPromiseResolve();
        }

        await nextTick();

        // Ensures that sections & addons are fully loaded and ready.
        if (currentInstance?.vnode) {
          // Wait for onBeforeEnter upwards to 3 times. This allows async components to load other async components.
          await recursivelyWaitForPromises(currentInstance.vnode, 'onBeforeEnter', 3);
        }

        contentRendered.value = true;

        model.setContentReady(true);
        onResize();
        sendEvent('campaign-ready');
      })();
    }

    let toolbarReadyPromiseResolve: (() => void) | undefined;
    let navigatorReadyPromiseResolve: (() => void) | undefined;

    const toolbarReadyPromise = new Promise<void>((resolve) => {
      toolbarReadyPromiseResolve = resolve;
    });

    const navigatorReadyPromise = new Promise<void>((resolve) => {
      navigatorReadyPromiseResolve = resolve;
    });

    const LFToolbar = defineAsyncComponent({
      loader: async () => {
        const component = await import('@/components/components/editing/LFToolbar.vue');

        if (toolbarReadyPromiseResolve) {
          toolbarReadyPromiseResolve();
        }

        return component;
      }
    });

    const LFNavigator = defineAsyncComponent({
      loader: async () => {
        const component = await import('@/components/components/editing/LFNavigator.vue');

        if (navigatorReadyPromiseResolve) {
          navigatorReadyPromiseResolve();
        }

        return component;
      }
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize);
      onResize();
      store.commit('utility/setCurrentUrl', (window.location.href + '').split('#')[0]);

      onEvent('response-url', (data: string) => {
        const extendedParams: Record<string, string> = {};
        const parsedData: IframeEventData = JSON.parse(data);
        const topLevelParams = parseQueryString(parsedData.data);
        const queryParams = getQueryParams();
        // Make sure we dont have dupes, if not, add it to extendedParams
        if (Object.keys(topLevelParams).length > 0) {
          for (let topLevelParam in topLevelParams) {
            if (
              Object.prototype.hasOwnProperty.call(topLevelParams, topLevelParam) &&
              typeof queryParams[`${topLevelParam}`] === 'undefined'
            ) {
              extendedParams[`${topLevelParam}`] = topLevelParams[`${topLevelParam}`];
            }
          }
        }

        if (parsedData && parsedData.data) {
          store.commit('utility/setCurrentUrl', (parsedData.data + '').split('#')[0]);
        }

        //Set the params in store
        store.dispatch('global/addStaticQueryParams', extendedParams);

        //After we have extended the params with top level params, we need to set replacement tags once again
        store.dispatch('global/setReplacementTags', getQueryParams());

        model.formFields.forEach((field) => field.setInitialValue());
      });

      //Send event
      sendEvent('request-url', undefined, true);
    }

    const preloaderStyles = computed(() => {
      if (campaignState.advanced?.preloader) {
        return campaignState.advanced.preloader;
      }
      return undefined;
    });

    const integrations = computed(() => {
      return (
        state.config?.integrations.filter((integration) => {
          return integration.hasVueComponent();
        }) ?? []
      );
    });

    const campaignTitle = computed(() => {
      return (
        campaignState.config?.metatags?.find((meta) => {
          return meta.property === 'og:title';
        })?.content ?? campaignState.config?.title
      );
    });

    const headtagsCollection = computed<HeadObject>(() => {
      const defaultMetatags: HeadAttrs[] = [];

      if (campaignState.config?.metatags) {
        campaignState.config?.metatags.forEach((tag) => {
          defaultMetatags.push(tag);
        });
      }
      return {
        meta: [
          { 'http-equiv': 'Content-Type', content: 'text/html; charset=UTF-8' },
          {
            name: 'viewport',
            content:
              'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no, minimal-ui'
          },
          {
            'http-equiv': 'Content-Language',
            content: campaignState.config?.languageCode ?? 'en-uk'
          },
          { name: 'google', content: 'notranslate' },
          { name: 'format-detection', content: 'telephone=no' },
          ...defaultMetatags
        ],
        link: [{ rel: 'canonical', href: campaignState.config?.campaignUrl }]
      };
    });

    useHead(headtagsCollection);

    if (
      campaignState.config &&
      typeof campaignState.config?.gameAlias !== 'undefined' &&
      campaignState.config?.gameAlias === 'landingpage'
    ) {
      useAnalytics().handlePageView();
    }

    return {
      isTemplatePreview,
      hidePreloader,
      preloaderStyles,
      isSimulating,
      showPrompt,
      promptHeading,
      promptDescription,
      promptButtons,
      handlePromptButtonClicked,
      onlyDevMode,
      model,
      onCampaignClick,
      state,
      ready,
      integrations,
      globalState: store.state,
      campaignStyle,
      isScrollIndicatorEnabled,
      colorScrollIndicator,
      isNavigatorDockRequest,
      isDocked,
      onCloseToolbar,
      onOpenToolbar,
      onRequestNavigatorDock,
      onSetDocking,
      sections,
      hasGameInitializer,
      isPopup,
      LFNavigator,
      isDemo,
      contentRendered,
      LFToolbar,
      gameInitializerComp,
      ActiveElementTypes,
      utilityStoreState: store.state.utility,
      campaignState,
      campaignTitle,
      showToolbar,
      onBeforeEnter: async () => {
        if (!isSsr()) {
          window.history.scrollRestoration = 'manual';
        }

        await readyPromise;

        if (!isSsr()) {
          // Toolbar
          if (showToolbar.value) {
            await toolbarReadyPromise;
          }

          if (
            route.query.edit === '1' &&
            state.isEditModeActive &&
            store.state.editing?.isNavigatorActive &&
            showToolbar.value
          ) {
            await navigatorReadyPromise;
          }
        }
      }
    };
  }
});
</script>

<style lang="scss">
// NEVER CHANGE THIS CLASS TO MATCH THE OLD FE
// WE SHOULD NOT ADD overflow: hidden; OR  min-height: 100vh; BROKE  NEW FE IMAGES AND STUFF
// IF YOU REALLY WANT TO CHANGE THIS TO MATCH THE OLD FE - THEN GO TO DANNIE AND HE WOULD BE SUPER HAPPY EXPLAIN FOR THE 100TH TIME WHY WE SHOULD NOT
.campaign {
  opacity: 0;

  &--enter-instant {
    opacity: 1 !important;
    animation: none !important;
  }
}

.site--simulate-mobile .campaign,
.site--simulate-tablet .campaign {
  overflow: hidden;
}
</style>
